import React, { FunctionComponent, useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import Client from '@spike/client-model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { Chip } from 'components/UI';
import clsx from 'clsx';
import Tag from 'components/UI/V2/Tag/Tag';
import { usePermissions } from 'hooks';
import Spinner from 'components/UI/V3/Spinner';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { ClientProfileOverview } from '@spike/client-model';

interface Props {
    client?: Client;
    displayNameFallback?: string;
    readOnly?: boolean;
    onRemoveClient: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            justifyContent: 'space-between',
            paddingBottom: 13,
            width: '100%',
            [theme.breakpoints.up('md')]: {
                paddingBottom: 15
            }
        },
        clientInfoContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '95%',
            paddingRight: 9,
            [theme.breakpoints.up('md')]: {
                flexDirection: 'row'
            }
        },
        iconContainer: {
            textAlign: 'right',
            width: '5%'
        },
        iconTrash: {
            cursor: 'pointer',
            fontSize: 18
        },
        title: {
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '16px',
            paddingBottom: 8
        },
        nameText: {
            paddingBottom: 0,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        nameRow: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'nowrap',
            minWidth: 0
        },
        owesPill: {
            color: '#CC444B',
            fontSize: 13,
            fontWeight: 500,
            borderRadius: 100,
            padding: '4px 10px',
            backgroundColor: '#FFE5E7',
            lineHeight: '20px',
            display: 'inline-flex',
            alignItems: 'center',
            position: 'relative',
            top: -2
        },
        text: {
            fontSize: 14,
            lineHeight: '14px',
            paddingBottom: 8,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            width: '100%',
            overflow: 'hidden',
            [theme.breakpoints.up('md')]: {
                paddingRight: 5
            }
        },
        tagsContainer: {
            gap: 6,
            display: 'flex',
            paddingTop: 3,
            minWidth: '30%',
            flexWrap: 'wrap',
            marginBottom: 'auto',
            [theme.breakpoints.down('sm')]: {
                width: '100%'
            }
        },
        infoContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '90%',
            [theme.breakpoints.up('md')]: {
                paddingRight: 5,
                maxWidth: '60%'
            }
        },
        expandedInfoContainer: {
            [theme.breakpoints.up('md')]: {
                width: '90% !important'
            }
        },
        nameOnlyRow: {
            flex: 1,
            minWidth: 0,
            paddingRight: 9,
            [theme.breakpoints.up('md')]: {
                paddingRight: 15
            }
        },
        tag: {
            'height': 24,
            'background': '#BCB8AE',
            'color': '#fff',
            '& .MuiChip-label': {
                fontSize: '13px !important'
            }
        },
        desktop: {
            [theme.breakpoints.down('sm')]: {
                display: 'none'
            }
        },
        mobile: {
            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        boxFlexRow: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            alignItems: 'center',
            height: '85px'
        }
    })
);

export const ClientResult: FunctionComponent<Props> = props => {
    const classes = useStyles(props);
    const permissions = usePermissions();
    const canViewSensitiveContact = permissions.clients.viewSensitiveContact;
    const profileOverview = useSelector<RootState, ClientProfileOverview | undefined>(
        state => state.clients.profileOverview
    );

    const parseMoney = (value: unknown): number => {
        if (typeof value === 'number') return value;
        if (typeof value !== 'string') return Number.NaN;
        return parseFloat(value.replace(/[^0-9.-]+/g, ''));
    };

    const getProfileCustomerId = (overview: ClientProfileOverview | undefined): number | undefined => {
        const anyOverview = overview as any;
        return (
            anyOverview?.customer?.id ??
            anyOverview?.customerId ??
            anyOverview?.customer?.customerId ??
            anyOverview?.customer?.clientId
        );
    };

    const clientOwes = useMemo(() => {
        if (!props.client?.id || !profileOverview) return false;

        const overviewCustomerId = getProfileCustomerId(profileOverview);
        if (overviewCustomerId != null && overviewCustomerId !== props.client.id) return false;

        const debt = (profileOverview as any)?.outstandingInvoices?.invoiceDebt;
        const amount = parseMoney(debt);
        return !Number.isNaN(amount) && amount > 0;
    }, [props.client?.id, profileOverview]);

    const nameForRestricted =
        (props.client ? `${props.client.firstName ?? ''} ${props.client.lastName ?? ''}`.trim() : '') ||
        props.displayNameFallback?.trim() ||
        '';

    const minimalNameOnlyView = (
        <Box className={classes.container}>
            <Box className={classes.nameOnlyRow}>
                <Box className={classes.nameRow}>
                    <Typography className={clsx(classes.title, classes.nameText)}>{nameForRestricted}</Typography>
                    {clientOwes && <span className={classes.owesPill}>Owes</span>}
                </Box>
            </Box>
            {!props.readOnly && (
                <Box
                    id="booking_img_delete"
                    className={classes.iconContainer}
                >
                    <FontAwesomeIcon
                        icon={faTrashCan}
                        className={classes.iconTrash}
                        onClick={props.onRemoveClient}
                    />
                </Box>
            )}
        </Box>
    );

    if (!canViewSensitiveContact) {
        if (!nameForRestricted) {
            return (
                <Box className={classes.boxFlexRow}>
                    <Spinner />
                </Box>
            );
        }
        return minimalNameOnlyView;
    }

    return (
        <Box>
            {!props.client ? (
                <Box className={classes.boxFlexRow}>
                    <Spinner />
                </Box>
            ) : (
                <Box className={classes.container}>
                    <Box className={classes.clientInfoContainer}>
                        <Box
                            className={clsx(classes.infoContainer, {
                                [classes.expandedInfoContainer]:
                                    props.client.tags?.positive?.length === 0 &&
                                    props.client.tags?.negative?.length === 0
                            })}
                        >
                            <Box className={classes.nameRow}>
                                <Typography className={clsx(classes.title, classes.nameText)}>
                                    {props.client.firstName} {props.client.lastName}
                                </Typography>
                                {clientOwes && <span className={classes.owesPill}>Owes</span>}
                            </Box>
                            {props.client?.phone && (
                                <Typography className={classes.text}>
                                    {`(${props.client?.phone?.slice(0, 3)}) ${props.client?.phone?.slice(
                                        3,
                                        6
                                    )}-${props.client?.phone?.slice(6, 10)}`}
                                </Typography>
                            )}
                            <Typography className={classes.text}>{props.client.email}</Typography>

                            {props.client.blocked && (
                                <div className={classes.desktop}>
                                    <Tag
                                        size="small"
                                        variant="danger"
                                    >
                                        Blocked
                                    </Tag>
                                </div>
                            )}
                        </Box>
                        {(props.client.tags?.positive?.length > 0 ||
                            props.client.tags?.negative?.length > 0 ||
                            props.client.blocked) && (
                            <Box className={classes.tagsContainer}>
                                {props.client.blocked && (
                                    <Tag
                                        size="small"
                                        variant="danger"
                                        className={classes.mobile}
                                    >
                                        Blocked
                                    </Tag>
                                )}
                                {props.client.tags?.positive.map(tag => {
                                    return (
                                        <Chip
                                            key={tag.id}
                                            label={tag.name}
                                            className={classes.tag}
                                        />
                                    );
                                })}
                                {props.client.tags?.negative.map(tag => {
                                    return (
                                        <Chip
                                            key={tag.id}
                                            label={tag.name}
                                            className={classes.tag}
                                        />
                                    );
                                })}
                            </Box>
                        )}
                    </Box>
                    {!props.readOnly && (
                        <Box
                            id="booking_img_delete"
                            className={classes.iconContainer}
                        >
                            <FontAwesomeIcon
                                icon={faTrashCan}
                                className={classes.iconTrash}
                                onClick={props.onRemoveClient}
                            />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ClientResult;
